import { useRef, useCallback, useEffect } from 'react';
import type { Canvas as CanvasType, FabricObject } from 'fabric';
import { getObjectName } from '../utils/fabricHelpers';
import { useWhiteboardStore } from '../store/useWhiteboardStore';

interface TouchPoint {
  id: number;
  x: number;
  y: number;
  startX: number;
  startY: number;
  startTime: number;
}

interface QuestionTouchMetadata {
  scale: number;
  left: number;
  top: number;
  isDragging: boolean;
  isPinching: boolean;
}

interface UseMobileQuestionTouchProps {
  fabricCanvasRef: React.MutableRefObject<CanvasType | null>;
  isMobile: boolean;
  questionMetadata: Record<string, { scale: number; left: number; top: number }>;
  setQuestionMetadata: React.Dispatch<React.SetStateAction<Record<string, { scale: number; left: number; top: number }>>>;
  updateOpticalChoicePositionsFrameBased?: (canvas: CanvasType, frame: any, imageScale: number) => void;
}

export const useMobileQuestionTouch = ({
  fabricCanvasRef,
  isMobile,
  questionMetadata,
  setQuestionMetadata,
  updateOpticalChoicePositionsFrameBased
}: UseMobileQuestionTouchProps) => {
  const touchesRef = useRef<Map<number, TouchPoint>>(new Map());
  const questionTouchStateRef = useRef<QuestionTouchMetadata | null>(null);
  // Removed lastPinchDistanceRef - not needed
  const initialPinchDistanceRef = useRef<number>(0);
  const initialScaleRef = useRef<number>(1);
  const questionGroupRef = useRef<FabricObject | null>(null);
  const originalFrameDimensionsRef = useRef<{ width: number; height: number } | null>(null);

  // Helper function to calculate correct frame dimensions
  const calculateFrameDimensions = useCallback((questionImg: FabricObject, parentImg: FabricObject | null, scale: number) => {
    const imgWidth = questionImg.width || 1;
    const imgHeight = questionImg.height || 1;
    
    // This must match exactly how useQuestionBackground calculates it
    let frameHeight = imgHeight * scale + 20; // Base height with padding
    
    if (parentImg) {
      const parentScale = scale * (imgWidth / (parentImg.width || 1));
      const parentHeight = (parentImg.height || 0) * parentScale;
      // Match useQuestionBackground: scaledParentHeight + spacingBetweenImages + imgHeight * scale + 20
      const spacingBetweenImages = 20; // Default spacing
      frameHeight = parentHeight + spacingBetweenImages + imgHeight * scale;
    }
    
    return {
      width: imgWidth * scale,
      height: frameHeight
    };
  }, []);

  // Helper function to get question-related objects as a group
  const getQuestionGroup = useCallback((canvas: CanvasType) => {
    const questionObjects: FabricObject[] = [];
    
    canvas.getObjects().forEach(obj => {
      const name = getObjectName(obj);
      if (name === 'questionBackground' || 
          name === 'parentQuestionBackground' ||
          name === 'questionFrame' ||
          name?.startsWith('questionOpticalChoice_')) {
        questionObjects.push(obj);
      }
    });

    return questionObjects;
  }, []);

  // Check if touch is on question container (prioritize question image)
  const isTouchOnQuestion = useCallback((canvas: CanvasType, x: number, y: number): boolean => {
    // First check the question image directly
    const questionImg = canvas.getObjects().find(obj => 
      getObjectName(obj) === 'questionBackground'
    );
    
    if (questionImg) {
      const imgBounds = questionImg.getBoundingRect();
      const padding = 30; // More padding for easier touch
      
      // Check parent image bounds too if exists
      const parentImg = canvas.getObjects().find(obj => 
        getObjectName(obj) === 'parentQuestionBackground'
      );
      
      let minTop = imgBounds.top;
      if (parentImg) {
        const parentBounds = parentImg.getBoundingRect();
        minTop = Math.min(minTop, parentBounds.top);
      }
      
      return x >= imgBounds.left - padding && 
             x <= imgBounds.left + imgBounds.width + padding &&
             y >= minTop - padding && 
             y <= imgBounds.top + imgBounds.height + padding;
    }
    
    // Fallback to frame if no question image
    const frame = canvas.getObjects().find(obj => 
      getObjectName(obj) === 'questionFrame'
    );
    
    if (!frame) return false;
    
    const bounds = frame.getBoundingRect();
    const padding = 20;
    return x >= bounds.left - padding && 
           x <= bounds.left + bounds.width + padding &&
           y >= bounds.top - padding && 
           y <= bounds.top + bounds.height + padding;
  }, []);

  // Handle touch start
  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!isMobile) return;
    
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    // Don't handle if drawing mode is active
    const { currentTool } = useWhiteboardStore.getState();
    if (currentTool !== 'select' && currentTool !== 'pan' as any) {
      return;
    }
    
    // Debug overlay update
    if ((window as any).debugTouch) {
      (window as any).debugTouch({
        touchCount: e.touches.length,
        message: `Tool: ${currentTool} Touch: ${e.touches.length}`,
        isPinching: false,
        scale: 1
      });
    }

    const touches = e.touches;
    touchesRef.current.clear();

    // Store all current touches
    for (let i = 0; i < touches.length; i++) {
      const touch = touches[i];
      const pointer = canvas.getPointer(touch as any);
      
      touchesRef.current.set(touch.identifier, {
        id: touch.identifier,
        x: pointer.x,
        y: pointer.y,
        startX: pointer.x,
        startY: pointer.y,
        startTime: Date.now()
      });
    }

    // Check if any touch is on the question
    const touchArray = Array.from(touchesRef.current.values());
    const isOnQuestion = touchArray.some(touch => 
      isTouchOnQuestion(canvas, touch.x, touch.y)
    );

    if (isOnQuestion) {
      e.preventDefault();
      e.stopPropagation();
      
      // Debug overlay update
      if ((window as any).debugTouch) {
        (window as any).debugTouch({
          message: 'Touch on question detected'
        });
      }

      // Get current question state from frame
      const frame = canvas.getObjects().find(obj => 
        getObjectName(obj) === 'questionFrame'
      );
      
      const questionImg = canvas.getObjects().find(obj => 
        getObjectName(obj) === 'questionBackground'
      );
      
      const parentImg = canvas.getObjects().find(obj => 
        getObjectName(obj) === 'parentQuestionBackground'
      );
      
      if (frame && questionImg) {
        const questionId = (frame as any).data?.questionId || '';
        
        // Always use the actual current scale from the image
        const currentScale = questionImg.scaleX || 1;
        const currentLeft = frame.left || 0;
        const currentTop = frame.top || 0;
        
        const currentMetadata = {
          scale: currentScale,
          left: currentLeft,
          top: currentTop
        };
        
        // Store frame dimensions WITHOUT locking
        const currentFrameHeight = frame.height || 0;
        const currentFrameWidth = frame.width || 0;
        const frameBounds = frame.getBoundingRect();
        
        
        // Store original dimensions but don't lock
        (frame as any)._originalHeight = currentFrameHeight;
        (frame as any)._originalWidth = currentFrameWidth;
        (frame as any)._originalBoundsHeight = frameBounds.height;
        (frame as any)._touchActive = true;
        
        // IMPORTANT: Store question image's current dimensions too
        if (questionImg) {
          const imgBounds = questionImg.getBoundingRect();
          (questionImg as any)._originalBoundsHeight = imgBounds.height;
          (questionImg as any)._originalScaleY = questionImg.scaleY;
          (questionImg as any)._originalHeight = questionImg.height;
          
          // Force image to maintain its current dimensions
          questionImg.setCoords();
          
        }
        
        // Update debug overlay
        if ((window as any).debugTouch) {
          const questionBounds = frame.getBoundingRect();
          const questionImg = canvas.getObjects().find(obj => 
            getObjectName(obj) === 'questionBackground'
          );
          const imgBounds = questionImg ? questionImg.getBoundingRect() : null;
          
          (window as any).debugTouch({
            message: `Frame H:${currentFrameHeight.toFixed(0)} BoundsH:${frameBounds.height.toFixed(0)} Diff:${(frameBounds.height - currentFrameHeight).toFixed(0)}`,
            scale: currentScale,
            questionBounds: {
              width: questionBounds.width,
              height: questionBounds.height,
              bottom: questionBounds.top + questionBounds.height
            }
          });
          
          // Log what's happening to the image
          if (imgBounds && questionImg) {
            const imgScaledHeight = (questionImg.height || 0) * (questionImg.scaleY || 1);
            
            // Send to overlay
            // Monitor what happens to the image
            const checkInterval = setInterval(() => {
              if (!questionImg || !(frame as any)._touchActive) {
                clearInterval(checkInterval);
                return;
              }
              
              const currentBounds = questionImg.getBoundingRect();
              const expectedHeight = (questionImg.height || 0) * (questionImg.scaleY || 1);
              const diff = currentBounds.height - expectedHeight;
              
              if (Math.abs(diff) > 1) {
                (window as any).debugTouch({
                  message: `IMG CHANGED! H=${questionImg.height} S=${questionImg.scaleY?.toFixed(3)} => ${expectedHeight.toFixed(0)} | Bounds=${currentBounds.height.toFixed(0)} | Diff=${diff.toFixed(0)}`
                });
                clearInterval(checkInterval);
              }
            }, 50);
            
            // Store interval for cleanup
            (questionImg as any)._checkInterval = checkInterval;
          }
        }
        
        
        // Monitor frame for changes
        const monitorFrame = () => {
          if ((window as any).debugFrame && frame) {
            (window as any).debugFrame(frame.width || 0, frame.height || 0);
          }
        };
        monitorFrame();
        
        // Debug overlay - show initial scale and canvas info
        if ((window as any).debugTouch) {
          const questionBounds = frame.getBoundingRect();
          
          (window as any).debugTouch({
            scale: currentScale,
            message: `Frame: ${frame.width?.toFixed(0)}x${frame.height?.toFixed(0)}`,
            questionBounds: {
              width: questionBounds.width,
              height: questionBounds.height,
              bottom: questionBounds.top + questionBounds.height
            }
          });
        }

        questionTouchStateRef.current = {
          scale: currentMetadata.scale,
          left: currentMetadata.left,
          top: currentMetadata.top,
          isDragging: touches.length === 1,
          isPinching: touches.length === 2
        };
        

        // Store initial pinch distance
        if (touches.length === 2) {
          const dx = touchArray[1].x - touchArray[0].x;
          const dy = touchArray[1].y - touchArray[0].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          initialPinchDistanceRef.current = distance;
          initialScaleRef.current = currentMetadata.scale;
          // Update debug overlay
          if ((window as any).debugTouch) {
            (window as any).debugTouch({
              touchCount: 2,
              isPinching: true,
              distance,
              scale: initialScaleRef.current,
              message: `Pinch init - scale: ${initialScaleRef.current.toFixed(3)}`
            });
          }
        }

        // Store question objects reference
        questionGroupRef.current = questionImg;
        
        // Store original frame dimensions if not already stored
        if (!originalFrameDimensionsRef.current && frame && questionImg) {
          // Calculate base frame dimensions
          // Frame should encompass the question image and parent if exists
          const imgWidth = questionImg.width || 1;
          const imgHeight = questionImg.height || 1;
          
          // Find parent image if exists
          const parentImg = canvas.getObjects().find(obj => 
            getObjectName(obj) === 'parentQuestionBackground'
          );
          
          // Don't include padding in base dimensions - padding is added after scaling
          let baseFrameHeight = imgHeight; // Base height WITHOUT padding
          
          // Add parent image height if exists
          if (parentImg) {
            const parentScale = (imgWidth / (parentImg.width || 1));
            const parentHeight = (parentImg.height || 0) * parentScale;
            baseFrameHeight = imgHeight + parentHeight + 20; // img + parent + spacing (no padding)
          }
          
          
          originalFrameDimensionsRef.current = {
            width: imgWidth, // Base width is image width
            height: baseFrameHeight // Store base height without padding
          };
        }
      }
    }
  }, [fabricCanvasRef, isMobile, isTouchOnQuestion, questionMetadata]);

  // Handle touch move
  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isMobile || !questionTouchStateRef.current) {
      return;
    }
    
    const canvas = fabricCanvasRef.current;
    if (!canvas || !questionGroupRef.current) {
      return;
    }
    
    // Check if canvas zoom is not 1 - this could interfere with our scaling
    const canvasZoom = canvas.getZoom();
    if (canvasZoom !== 1) {
      console.warn('Canvas zoom is not 1:', canvasZoom, '- this may cause scaling issues');
    }

    e.preventDefault();
    e.stopPropagation();

    const touches = e.touches;
    
    // Update touch positions and add new touches
    for (let i = 0; i < touches.length; i++) {
      const touch = touches[i];
      const pointer = canvas.getPointer(touch as any);
      const touchData = touchesRef.current.get(touch.identifier);
      
      if (touchData) {
        touchData.x = pointer.x;
        touchData.y = pointer.y;
      } else {
        // New touch added during move (e.g., second finger for pinch)
        touchesRef.current.set(touch.identifier, {
          id: touch.identifier,
          x: pointer.x,
          y: pointer.y,
          startX: pointer.x,
          startY: pointer.y,
          startTime: Date.now()
        });
      }
    }
    
    // Update gesture state if touch count changed
    if (questionTouchStateRef.current) {
      const wasDragging = questionTouchStateRef.current.isDragging;
      const wasPinching = questionTouchStateRef.current.isPinching;
      const nowDragging = touches.length === 1;
      const nowPinching = touches.length === 2;
      
      if (wasDragging && nowPinching) {
        // Switched from drag to pinch
        questionTouchStateRef.current.isDragging = false;
        questionTouchStateRef.current.isPinching = true;
        // Reset pinch distance for new gesture
        initialPinchDistanceRef.current = 0;
      } else if (wasPinching && nowDragging) {
        // Switched from pinch to drag
        questionTouchStateRef.current.isDragging = true;
        questionTouchStateRef.current.isPinching = false;
      }
    }

    const touchArray = Array.from(touchesRef.current.values());
    const questionImg = questionGroupRef.current;
    const questionId = (questionImg as any).data?.questionId || '';

    // Single finger drag
    if (touches.length === 1 && questionTouchStateRef.current.isDragging) {
      const touch = touchArray[0];
      const deltaX = touch.x - touch.startX;
      const deltaY = touch.y - touch.startY;
      

      const newLeft = questionTouchStateRef.current.left + deltaX;
      const newTop = questionTouchStateRef.current.top + deltaY;

      // Apply boundaries based on frame
      const frame = canvas.getObjects().find(obj => 
        getObjectName(obj) === 'questionFrame'
      );
      const bounds = frame ? frame.getBoundingRect() : questionImg.getBoundingRect();
      const maxLeft = canvas.width! - bounds.width;
      const maxTop = canvas.height! - bounds.height;

      const constrainedLeft = Math.max(0, Math.min(newLeft, maxLeft));
      const constrainedTop = Math.max(0, Math.min(newTop, maxTop));

      // Update all question-related objects
      updateQuestionPosition(canvas, constrainedLeft, constrainedTop);

      // Update metadata
      setQuestionMetadata(prev => ({
        ...prev,
        [questionId]: {
          ...prev[questionId],
          left: constrainedLeft,
          top: constrainedTop
        }
      }));
    }
    
    // Two finger pinch
    else if (touches.length === 2) {
      // Switch to pinch mode if not already
      if (!questionTouchStateRef.current.isPinching) {
        questionTouchStateRef.current.isPinching = true;
        questionTouchStateRef.current.isDragging = false;
      }
      
      if (touchArray.length < 2) {
        return;
      }
      
      const dx = touchArray[1].x - touchArray[0].x;
      const dy = touchArray[1].y - touchArray[0].y;
      const currentDistance = Math.sqrt(dx * dx + dy * dy);
      
      
      // If this is a new pinch gesture (no initial distance), set it up
      if (initialPinchDistanceRef.current === 0) {
        initialPinchDistanceRef.current = currentDistance;
        // Get current scale from the question image
        const currentScale = questionImg.scaleX || 1;
        initialScaleRef.current = currentScale;
        questionTouchStateRef.current.scale = currentScale;
        // Update debug overlay
        if ((window as any).debugTouch) {
          (window as any).debugTouch({
            scale: currentScale,
            message: `New pinch - init scale: ${currentScale.toFixed(3)}`
          });
        }
        return; // Don't calculate scale on first setup
      }
      
      if (initialPinchDistanceRef.current > 0) {
        // Calculate scale based on initial distance
        const distanceRatio = currentDistance / initialPinchDistanceRef.current;
        const newScale = initialScaleRef.current * distanceRatio;
        
        // Constrain scale - allow smaller scales for mobile
        const constrainedScale = Math.max(0.1, Math.min(2, newScale));
        
        
        // Always apply scale during pinch
        const currentImgScale = questionImg.scaleX || 1;
        
        
        // Update debug overlay with calculation details
        if ((window as any).debugTouch) {
          const frame = canvas.getObjects().find(obj => getObjectName(obj) === 'questionFrame');
          const questionImg = canvas.getObjects().find(obj => getObjectName(obj) === 'questionBackground');
          const parentImg = canvas.getObjects().find(obj => getObjectName(obj) === 'parentQuestionBackground');
          
          let frameInfo = '';
          if (frame && questionImg) {
            const currH = frame.height || 0;
            const expectedDims = calculateFrameDimensions(questionImg, parentImg || null, constrainedScale);
            const diff = currH - expectedDims.height;
            const frameBounds = frame.getBoundingRect();
            frameInfo = `H: ${currH.toFixed(0)} Expected: ${expectedDims.height.toFixed(0)} Diff: ${diff.toFixed(0)}`;
            
            // Update frame monitor
            if ((window as any).debugFrame) {
              (window as any).debugFrame(frame.width || 0, frame.height || 0);
            }
          }
          
          (window as any).debugTouch({
            scale: constrainedScale,
            distance: currentDistance,
            message: frameInfo || `Scale: ${constrainedScale.toFixed(3)}`,
            questionBounds: frame ? (() => {
              const bounds = frame.getBoundingRect();
              return {
                width: bounds.width,
                height: bounds.height,
                bottom: bounds.top + bounds.height
              };
            })() : undefined
          });
        }
        
        
        // Calculate pinch center
        const centerX = (touchArray[0].x + touchArray[1].x) / 2;
        const centerY = (touchArray[0].y + touchArray[1].y) / 2;
        
        // Update scale
        updateQuestionScale(canvas, constrainedScale, centerX, centerY);
        
        // Update state
        questionTouchStateRef.current.scale = constrainedScale;
        
        // Update metadata
        setQuestionMetadata(prev => ({
          ...prev,
          [questionId]: {
            ...prev[questionId],
            scale: constrainedScale
          }
        }));
      }
      
      // Update last distance is not needed
    }

    canvas.requestRenderAll();
  }, [fabricCanvasRef, isMobile, setQuestionMetadata]);

  // Handle touch end
  const handleTouchEnd = useCallback((e: TouchEvent) => {
    if (!isMobile) return;

    // Remove ended touches
    const changedTouches = e.changedTouches;
    for (let i = 0; i < changedTouches.length; i++) {
      touchesRef.current.delete(changedTouches[i].identifier);
    }

    // Reset state if no more touches
    if (touchesRef.current.size === 0) {
      // Clean up frame and image touch state
      const canvas = fabricCanvasRef.current;
      if (canvas) {
        const frame = canvas.getObjects().find(obj => 
          getObjectName(obj) === 'questionFrame'
        );
        if (frame && (frame as any)._touchActive) {
          (frame as any)._touchActive = false;
          delete (frame as any)._originalHeight;
          delete (frame as any)._originalWidth;
        }
        
        // Unlock question image height
        const questionImg = canvas.getObjects().find(obj => 
          getObjectName(obj) === 'questionBackground'
        );
        if (questionImg && (questionImg as any)._originalHeight !== undefined) {
          // Clear monitoring interval
          if ((questionImg as any)._checkInterval) {
            clearInterval((questionImg as any)._checkInterval);
            delete (questionImg as any)._checkInterval;
          }
          
          delete (questionImg as any)._originalBoundsHeight;
          delete (questionImg as any)._originalScaleY;
          delete (questionImg as any)._originalHeight;
        }
      }
      
      questionTouchStateRef.current = null;
      // Reset handled below
      initialPinchDistanceRef.current = 0;
      // Don't reset scale to 1 - keep the last scale value
      questionGroupRef.current = null;
      originalFrameDimensionsRef.current = null; // Reset frame dimensions
    }

    // Handle single finger update
    if (touchesRef.current.size === 1 && questionTouchStateRef.current) {
      questionTouchStateRef.current.isDragging = true;
      questionTouchStateRef.current.isPinching = false;
      
      // Update start position for remaining touch
      const remainingTouch = Array.from(touchesRef.current.values())[0];
      remainingTouch.startX = remainingTouch.x;
      remainingTouch.startY = remainingTouch.y;
      
      // Reset pinch-related values but keep current scale
      // Reset handled below
      initialPinchDistanceRef.current = 0;
      // Don't reset initialScaleRef - we need to keep the current scale
    }
  }, [isMobile, fabricCanvasRef]);

  // Helper function to update question position
  const updateQuestionPosition = useCallback((canvas: CanvasType, left: number, top: number) => {
    const objects = getQuestionGroup(canvas);
    const questionImg = objects.find(obj => getObjectName(obj) === 'questionBackground');
    const parentImg = objects.find(obj => getObjectName(obj) === 'parentQuestionBackground');
    
    
    if (!questionImg) return;

    // Calculate offset for parent image if exists
    let questionTop = top;
    if (parentImg) {
      const parentHeight = (parentImg.height || 0) * (parentImg.scaleY || 1);
      const spacing = 20;
      questionTop = top + parentHeight + spacing;
    }

    // Update positions
    objects.forEach(obj => {
      const name = getObjectName(obj);
      
      if (name === 'questionFrame') {
        obj.set({ left, top });
        obj.setCoords(); // Update bounding box for touch detection
      } else if (name === 'parentQuestionBackground') {
        obj.set({ left, top });
        obj.setCoords();
      } else if (name === 'questionBackground') {
        obj.set({ left, top: questionTop });
        obj.setCoords();
      } else if (name?.startsWith('questionOpticalChoice_')) {
        // Skip optical choices here - they will be updated by updateOpticalChoicePositionsFrameBased
        // The original calculation here was incorrect anyway
        return;
      }
    });

    // Update optical choices using the frame-based positioning system
    // This needs to be done after all other objects are positioned
    if (updateOpticalChoicePositionsFrameBased && questionImg && questionImg.scaleX) {
      // Find the frame to pass to the update function
      const frame = objects.find(obj => getObjectName(obj) === 'questionFrame');
      if (frame) {
        updateOpticalChoicePositionsFrameBased(canvas, frame, questionImg.scaleX);
      }
    }

    // Force canvas to re-render
    canvas.requestRenderAll();
    
    // Update bounding boxes for all optical choices after movement
    canvas.getObjects().forEach(obj => {
      const objName = getObjectName(obj);
      if (objName?.startsWith('questionOpticalChoice_')) {
        obj.setCoords();
      }
    });
    
    // No resize handle on mobile - using pinch-to-zoom instead
  }, [getQuestionGroup, updateOpticalChoicePositionsFrameBased]);

  // Helper function to update question scale
  const updateQuestionScale = useCallback((
    canvas: CanvasType, 
    scale: number, 
    centerX: number, 
    centerY: number
  ) => {
    const objects = getQuestionGroup(canvas);
    const questionImg = objects.find(obj => getObjectName(obj) === 'questionBackground');
    const parentImg = objects.find(obj => getObjectName(obj) === 'parentQuestionBackground');
    
    if (!questionImg) return;
    
    // Find frame for updating
    const frame = objects.find(obj => getObjectName(obj) === 'questionFrame');

    const oldScale = questionImg.scaleX || 1;
    const scaleFactor = scale / oldScale;
    
    // Check if we're actually changing the scale
    if (Math.abs(scale - oldScale) < 0.001) {
      console.log('Scale unchanged, skipping update');
      return;
    }
    
    console.log('updateQuestionScale called:', {
      newScale: scale,
      oldScale,
      scaleFactor,
      centerX,
      centerY,
      frameCurrentWidth: frame?.width,
      frameCurrentHeight: frame?.height
    });
    
    // First update frame
    if (frame) {
      const oldLeft = frame.left || 0;
      const oldTop = frame.top || 0;
      
      // Calculate new dimensions
      const newDimensions = calculateFrameDimensions(questionImg, parentImg || null, scale);
      const newWidth = newDimensions.width;
      const newHeight = newDimensions.height;
      
      // Scale position around pinch center
      const newLeft = centerX - (centerX - oldLeft) * scaleFactor;
      const newTop = centerY - (centerY - oldTop) * scaleFactor;
      
      // Ensure frame stays within canvas bounds
      const constrainedLeft = Math.max(0, Math.min(newLeft, canvas.width! - newWidth));
      const constrainedTop = Math.max(0, Math.min(newTop, canvas.height! - newHeight));
      
      // Update frame
      frame.set({
        left: constrainedLeft,
        top: constrainedTop,
        width: newWidth,
        height: newHeight
      });
      frame.setCoords(); // This is already here, good!
    }
    
    // Update other objects relative to frame
    objects.forEach(obj => {
      const name = getObjectName(obj);
      
      if (name === 'questionBackground' && frame) {
        const oldLeft = obj.left || 0;
        const oldTop = obj.top || 0;
        
        // Position relative to frame
        let questionTop = frame.top || 0;
        if (parentImg) {
          const parentScale = scale * ((questionImg.width || 1) / (parentImg.width || 1));
          const parentHeight = (parentImg.height || 0) * parentScale;
          questionTop = frame.top! + parentHeight + 20; // spacing
        }
        
        obj.set({
          scaleX: scale,
          scaleY: scale,
          left: frame.left,
          top: questionTop
        });
        obj.setCoords();
      } else if (name === 'parentQuestionBackground' && parentImg && questionImg && frame) {
        // Parent image needs special scaling to maintain proportion with question image
        const parentScale = scale * ((questionImg.width || 1) / (parentImg.width || 1));
        
        // Position relative to frame
        obj.set({
          scaleX: parentScale,
          scaleY: parentScale,
          left: frame.left,
          top: frame.top
        });
        obj.setCoords();
      } else if (name === 'questionFrame') {
        // Frame already updated above, skip
      } else if (name?.startsWith('questionOpticalChoice_')) {
        // Skip optical choices here - they will be updated by updateOpticalChoicePositionsFrameBased
        return;
      }
    });

    // Update optical choices using the frame-based positioning system
    if (updateOpticalChoicePositionsFrameBased && questionImg && scale) {
      const frame = objects.find(obj => getObjectName(obj) === 'questionFrame');
      if (frame) {
        updateOpticalChoicePositionsFrameBased(canvas, frame, scale);
      }
    }

    // Force canvas to re-render
    canvas.requestRenderAll();
    
    // Update bounding boxes for all optical choices after scaling
    canvas.getObjects().forEach(obj => {
      const objName = getObjectName(obj);
      if (objName?.startsWith('questionOpticalChoice_')) {
        obj.setCoords();
      }
    });
    
    // No resize handle on mobile - using pinch-to-zoom instead
  }, [getQuestionGroup, updateOpticalChoicePositionsFrameBased]);

  // Add touch event listeners
  useEffect(() => {
    if (!isMobile) return;

    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const upperCanvasEl = canvas.upperCanvasEl;
    if (!upperCanvasEl) return;

    upperCanvasEl.addEventListener('touchstart', handleTouchStart, { passive: false });
    upperCanvasEl.addEventListener('touchmove', handleTouchMove, { passive: false });
    upperCanvasEl.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      upperCanvasEl.removeEventListener('touchstart', handleTouchStart);
      upperCanvasEl.removeEventListener('touchmove', handleTouchMove);
      upperCanvasEl.removeEventListener('touchend', handleTouchEnd);
    };
  }, [fabricCanvasRef, isMobile, handleTouchStart, handleTouchMove, handleTouchEnd]);

  return {
    // Exposed for external use if needed
    isQuestionBeingManipulated: () => questionTouchStateRef.current !== null
  };
};